import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CheckCircle2, XCircle, HelpCircle, X, ArrowUpRight } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import type { NotificationRecord } from '../services/notificationsApi'
import { AutoDismissCloseButton } from './AutoDismissCloseButton'

interface QualificationResultCardProps {
  notification: NotificationRecord
  onDismiss: (id: string) => void
  // Only the floating tray sets this — it's an ephemeral toast that should
  // count down and hide itself. The bell panel's persistent list renders
  // this same component without it: a plain close button, no timer, stays
  // until the person explicitly dismisses it (NotificationSidePanel.tsx).
  autoDismissMs?: number
  // Called after "Go to chat" navigates — the side panel passes this to also
  // close itself (mirrors onStartAction's close for lead notifications); the
  // tray has nothing extra to do, so it's optional.
  onAfterGoToChat?: () => void
  // Fires once this card's own exit animation completes — auto-dismiss or
  // manual close alike. Only the tray sets this; see NotificationTray.tsx's
  // activeIds for why the tray needs this signal instead of re-deriving from
  // the notifications list on every poll.
  onHidden?: () => void
}

// One card, used in both the floating tray and the notification side panel.
// Kept quiet on purpose: a single small status pill instead of a shouting
// all-caps colored banner, one muted meta line instead of an icon per field,
// and a clamped summary so the card's height stays predictable in a list.
export const QualificationResultCard: React.FC<QualificationResultCardProps> = ({
  notification,
  onDismiss,
  autoDismissMs,
  onAfterGoToChat,
  onHidden,
}) => {
  const { currentTheme: t } = useTheme()
  const navigate = useNavigate()
  const [dismissing, setDismissing] = useState(false)
  const [visible, setVisible] = useState(true)
  const lead = notification.lead
  const qualification = notification.qualification
  const autoDismissTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const STATUS_META = {
    qualified: { label: 'Qualified', bg: t.successBackground, color: t.successText, Icon: CheckCircle2 },
    unqualified: { label: 'Unqualified', bg: t.errorBackground, color: t.errorText, Icon: XCircle },
    pending: { label: 'Needs review', bg: t.warningBackground, color: t.warningText, Icon: HelpCircle },
  } as const

  // Auto-dismiss hides the toast locally only — it never calls onDismiss, so
  // the underlying notification stays unread and still shows in the panel's
  // list. Only an explicit close click actually dismisses it.
  useEffect(() => {
    if (!autoDismissMs) return
    autoDismissTimeoutRef.current = setTimeout(() => {
      setDismissing(true)
      setTimeout(() => {
        setVisible(false)
        onHidden?.()
      }, 250)
    }, autoDismissMs)
    return () => { if (autoDismissTimeoutRef.current) clearTimeout(autoDismissTimeoutRef.current) }
    // `onHidden` intentionally excluded — same reasoning as
    // LeadNotificationCard.tsx: depending on it would reschedule the
    // countdown on every unrelated re-render, and it's safe even if stale
    // (NotificationTray.tsx's handleHidden only does a functional setState).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoDismissMs])

  if (!visible || !qualification) return null

  const meta = STATUS_META[qualification.status]
  const leadLabel = lead?.name || lead?.email || (lead ? `Lead ${lead.id}` : 'Lead')
  const metaLine = [lead?.email, lead?.company].filter(Boolean).join(' · ')

  const handleDismiss = () => {
    if (autoDismissTimeoutRef.current) clearTimeout(autoDismissTimeoutRef.current)
    setDismissing(true)
    setTimeout(() => {
      setVisible(false)
      onDismiss(notification.id)
      onHidden?.()
    }, 250)
  }

  // Plain navigation, deliberately not the leadNotification-seeded state
  // ChatPage/handleLeadAction use — that copy is written for "a lead has
  // just been assigned" and would be wrong framing for a qualification
  // result. This just takes you to chat; it doesn't dismiss the notification.
  const handleGoToChat = () => {
    navigate('/dashboard/chat')
    onAfterGoToChat?.()
  }

  return (
    <div
      className="rounded-2xl p-4 flex flex-col gap-2.5"
      style={{
        backgroundColor: t.sectionBackground,
        border: `1px solid ${t.border}`,
        opacity: dismissing ? 0 : 1,
        transform: dismissing ? 'translateY(-4px)' : 'translateY(0)',
        transition: 'opacity 0.25s ease, transform 0.25s ease',
        width: '100%',
        // Only the floating tray needs a cap — a toast shouldn't grow to
        // fill the whole screen. In the side panel (autoDismissMs unset)
        // this should fill the panel's own width instead of leaving a gap.
        ...(autoDismissMs ? { maxWidth: '380px' } : {}),
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-[13.5px] font-semibold truncate" style={{ color: t.text }}>{leadLabel}</p>
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-semibold shrink-0"
              style={{ backgroundColor: meta.bg, color: meta.color }}
            >
              <meta.Icon size={10} />
              {meta.label}
            </span>
          </div>
          {metaLine && (
            <p className="text-[12px] truncate mt-0.5" style={{ color: t.mutedText }}>{metaLine}</p>
          )}
        </div>

        {autoDismissMs ? (
          <AutoDismissCloseButton
            durationMs={autoDismissMs}
            color={t.mutedText}
            trackColor={t.border}
            onClick={handleDismiss}
            ariaLabel="Dismiss notification"
          />
        ) : (
          <button
            onClick={handleDismiss}
            aria-label="Dismiss notification"
            className="p-1 rounded-md shrink-0"
            style={{ color: t.mutedText }}
          >
            <X size={14} />
          </button>
        )}
      </div>

      <p
        className="text-[12.5px] leading-relaxed"
        style={{
          color: t.secondrytext,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {qualification.summary}
      </p>

      <button
        onClick={handleGoToChat}
        className="flex items-center gap-1 self-start text-[12px] font-semibold"
        style={{ color: t.accent }}
      >
        Go to chat
        <ArrowUpRight size={12} />
      </button>
    </div>
  )
}
